import React, { Fragment, useMemo } from 'react';

import { CSSObject } from '@emotion/serialize';
import { useSelector } from 'react-redux';

import { CSSObjectWithLabel } from '@atlaskit/react-select';
import Select, {
  CheckboxSelect,
  ControlProps,
  OptionType,
} from '@atlaskit/select';
import SmartUserPicker, {
  DefaultValue,
  Value,
  Props as SmartPickerProps,
} from '@atlaskit/smart-user-picker';
import { colors, gridSize } from '@atlaskit/theme';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { AppData } from 'src/app/data';
import { srOnlyStyles } from 'src/components/accessibility';
import SearchField from 'src/components/search-field';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useIntl } from 'src/hooks/intl';
import { getCurrentWorkspace } from 'src/redux/profile/selectors';
import messages from 'src/sections/repository/sections/pull-request-list/components/i18n';
import {
  Filter,
  FilterGroup,
  subtleSelector,
} from 'src/sections/repository/styles/filters';
import { ProjectsFilter } from 'src/sections/workspace/components/workspace-filters/projects-filter';
import { WorkspaceProjectFilterContainer } from 'src/sections/workspace/components/workspace-filters/projects-filter-provider';
import { selectControlStyles } from 'src/styles/select';

import authorFilterMessages from '../pullrequests.i18n';
import { FilterContainer, prFilterSelectStyle } from '../styles/filter';
import { PRFilterState, PRUserFilterType } from '../types';

export type FilterProps = {
  user: AppData['user'];
  search: string;
  prState?: string;
  prStateAllChecked?: boolean;
  prUserFilter: PRUserFilterType;
  author: string;
  projectUuid: string;
  onChangeSearch: (search: string) => void;
  onChangePrState: (
    prStates: PRFilterState[],
    prStateAllChecked?: boolean
  ) => void;
  onChangeUserFilter: (prUserFilter: PRUserFilterType) => void;
  onChangeAuthor: (a: Value) => void;
  onChangeProject: (uuid: string | undefined) => void;
};

const WorkspacePullRequestsFilter = ({
  author,
  prUserFilter,
  onChangeAuthor,
  onChangeUserFilter,
  onChangePrState,
  onChangeSearch,
  onChangeProject,
  prState = 'OPEN+DRAFT',
  prStateAllChecked = false,
  search,
  projectUuid,
  user,
}: FilterProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const areDraftPRsEnabled = useStatsigGate(StatsigFeatureKeys.bbcDraftPRs);

  const prStateOptions = useMemo(() => {
    const states = [
      {
        label: formatMessage(messages.stateOpen),
        value: 'OPEN',
      },
      {
        label: formatMessage(messages.stateMerged),
        value: 'MERGED',
      },
      {
        label: formatMessage(messages.stateDeclined),
        value: 'DECLINED',
      },
    ];
    if (areDraftPRsEnabled) {
      states.push({
        label: formatMessage(messages.stateDraft),
        value: 'DRAFT',
      });
    }
    return states;
  }, [areDraftPRsEnabled, formatMessage]);

  const userPrOptions = useMemo(
    () => [
      {
        label: formatMessage(messages.userFilterReviewing),
        value: 'REVIEWING',
      },
      {
        label: formatMessage(messages.userFilterWatching),
        value: 'WATCHING',
      },
      {
        label: formatMessage(messages.userFilterAll),
        value: 'ALL',
      },
    ],
    [formatMessage]
  );

  const handleSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
    onChangeSearch(e.currentTarget.value);
  };

  const clearSearchFilter = () => {
    onChangeSearch('');
  };

  const handlePrStateChange = (option: OptionType[]) => {
    const states = option.map(({ value }) => value as PRFilterState);
    if (states.length === 0) {
      onChangePrState(['ALL']);
    } else if (states.length === prStateOptions.length) {
      onChangePrState(['ALL'], true);
    } else {
      onChangePrState(states);
    }
  };

  const handleUserFilterChange = ({ value }: { [key: string]: any }) => {
    onChangeUserFilter(value);
  };

  const handleProjectChange = (uuid: string | undefined) => {
    onChangeProject(uuid);
  };

  const searchPlaceholder = formatMessage(messages.filterDescription);

  const prStateValue = useMemo(() => {
    const states = prState?.split('+');
    // Handle ALL state specially since it is not a option that can be selected
    if (states.indexOf('ALL') !== -1) {
      return prStateAllChecked ? prStateOptions : [];
    }
    return states?.map(state => prStateOptions.find(o => o.value === state)!);
  }, [prState, prStateAllChecked, prStateOptions]);

  const userPrFilterValue = useMemo(
    () =>
      userPrOptions.filter(({ value }) => value === prUserFilter)[0] ||
      userPrOptions[0],
    [userPrOptions, prUserFilter]
  );

  const currentWorkspace = useSelector(getCurrentWorkspace);
  const { uuid: currentWorkspaceUuid } = currentWorkspace || {};

  const smartPickerProps: SmartPickerProps = useMemo(() => {
    const authorIds = author?.split('+') || [];

    const defaultAuthor: DefaultValue =
      authorIds.length > 0
        ? authorIds.map((authorId: string) => ({
            id: authorId.replace(/(^{)|(}$)/g, ''),
            type: 'user',
          }))
        : undefined;

    const pickerWidth = author ? gridSize() * 45 : gridSize() * 20;

    const handleAuthorChange = (authors: Value) => {
      onChangeAuthor(authors);
    };

    return {
      inputId: 'pull-request-author-picker',
      productKey: 'bitbucket',
      appearance: 'compact',
      onChange: handleAuthorChange,
      defaultValue: defaultAuthor,
      debounceTime: 400,
      fieldId: 'BitbucketWorkspacePullRequestAuthors',
      maxOptions: 20,
      prefetch: false,
      isClearable: true,
      noBorder: true,
      productAttributes: {
        emailDomain: 'Context',
        workspaceIds: currentWorkspaceUuid
          ? [currentWorkspaceUuid.replace(/[{}]/g, '')]
          : [],
      },
      principalId: 'Context',
      searchQueryFilter:
        'account_status:"active" AND (NOT email_domain:"connect.atlassian.com") AND (NOT account_type:app) * ',
      siteId: 'bitbucket',
      placeholder: formatMessage(
        authorFilterMessages.pullRequestsPageAuthorFilterPlaceholder
      ),
      addMoreMessage: formatMessage(
        authorFilterMessages.pullRequestsPageAuthorFilterAddMoreMessage
      ),
      isMulti: true,
      maxPickerHeight: gridSize() * 8,
      styles: {
        container: css => ({
          ...css,
          width: pickerWidth,
          '> div': {
            width: 'auto',
            height: 'auto',
          },
        }),
        control: css => ({
          ...css,
          backgroundColor: 'transparent',
          width: pickerWidth,
          borderWidth: '2px',
          '> div > div': {
            paddingLeft: '2px',
          },
          ':focus-within': {
            ...(css as { ':focus-within': React.CSSProperties })[
              ':focus-within'
            ],
            boxShadow: 'none',
          },
        }),
        menu: css => ({
          ...css,
          zIndex: 10,
          minWidth: pickerWidth,
        }),
        placeholder: css => ({
          ...css,
          color: `var(--ds-text, ${colors.N400})`,
        }),
      },
    };
  }, [currentWorkspaceUuid, formatMessage, onChangeAuthor, author]);

  return (
    <FilterContainer>
      <FilterGroup>
        <SearchField
          currentSearch={search}
          handleFilterChange={handleSearchChange}
          placeholder={searchPlaceholder}
          clearFilter={clearSearchFilter}
          fieldName="search-pull-requests-table-input"
        />
        <WorkspaceProjectFilterContainer>
          <ProjectsFilter
            projectUuid={projectUuid}
            onChange={handleProjectChange}
          />
        </WorkspaceProjectFilterContainer>
        <Filter
          mobileOrder={1}
          data-qa="select-pull-request-state"
          isFirstFilterElement
        >
          <CheckboxSelect
            isMulti
            isClearable
            placeholder={formatMessage(messages.statusPlaceholder)}
            appearance="subtle"
            test-id="workspace-pull-requests-state-select"
            options={prStateOptions}
            isSearchable={false}
            value={prStateValue}
            onChange={handlePrStateChange}
            styles={{
              menu: css => ({
                ...css,
                zIndex: 10,
                minWidth: 150,
              }),
            }}
          />
        </Filter>
        <Filter data-qa="author-selector" mobileOrder={2}>
          <label className={srOnlyStyles} htmlFor={smartPickerProps.inputId}>
            {smartPickerProps.placeholder}
          </label>
          <SmartUserPicker {...smartPickerProps} />
        </Filter>
        {!!user && (
          <Fragment>
            <Filter
              mobileOrder={1}
              data-testid="select-pull-request-user-filter"
            >
              <Select
                data-testid="workspace-pull-requests-user-filter-select"
                options={userPrOptions}
                isSearchable={false}
                value={userPrFilterValue}
                onChange={handleUserFilterChange}
                styles={{
                  ...prFilterSelectStyle,
                  control: (
                    css: CSSObjectWithLabel,
                    state: ControlProps<
                      {
                        label: string;
                        value: string;
                      },
                      false
                    >
                  ): CSSObject => ({
                    ...css,
                    ...subtleSelector(css, state),
                    ...selectControlStyles,
                    minWidth: gridSize() * 18,
                  }),
                }}
              />
            </Filter>
          </Fragment>
        )}
      </FilterGroup>
    </FilterContainer>
  );
};

export default WorkspacePullRequestsFilter;
